import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Typography } from '@mui/material'

import { useGetTrackedGameQuery } from '../../../api/core'
import { LockedFeature } from '../../../components/LockedFeature/LockedFeature'
import { LockedFeatureId } from '../../../types/LockedFeature'
import { UserLanguage } from '../../account/types/User'
import { Game } from '../../game'
import { LiveEventsList } from '../../implementation-examples/components/LiveEventExamples/LiveEventsList/LiveEventsList'
import { LiveEventsViewFilters } from '../../live-events/components/LiveEventsViewFilters/LiveEventsViewFilters'
import { useLiveEventExamplesSearchParams } from '../../live-events/hooks/useLiveEventExamplesSearchParams'
import { useLiveEventsCommonFilters } from '../../live-events/hooks/useLiveEventsCommonFilters'
import { useLiveEventsFilteredData } from '../../live-events/hooks/useLiveEventsFilteredData'
import { useDefaultLiveEvents } from '../../live-events/hooks/useTrackedGamesEvents'

type LiveEventExamplesSubtabProps = {
  game: Game
  userLanguage: UserLanguage
  hasAccessToLiveEventsTracker: boolean
  isGameTracked: boolean
}

const LiveEventExamplesSubtab: React.FC<LiveEventExamplesSubtabProps> = ({ game, userLanguage, hasAccessToLiveEventsTracker, isGameTracked }) => {
  const { t } = useTranslation()

  // Live events filters stored in URL params for bookmark persistence
  const { setSearchParams, parsedParams } = useLiveEventExamplesSearchParams()

  // Live Events data hooks
  const { currentData: trackedGame, isFetching: isTrackedGameLoading } = useGetTrackedGameQuery(
    { gameId: game.id, userLanguage },
    { skip: !isGameTracked || !hasAccessToLiveEventsTracker }
  )

  const trackedGameArray = useMemo(() => {
    if (trackedGame && trackedGame.allowed) {
      return [trackedGame]
    }
    return []
  }, [trackedGame])

  const gameIdArray = useMemo(() => {
    if (trackedGame && trackedGame.allowed) {
      return [trackedGame.game.id]
    }
    return []
  }, [trackedGame])

  const trackedEvents = useDefaultLiveEvents({ trackedGameIds: gameIdArray })

  // Common filters for live events
  const {
    commonFilters,
    commonFiltersData: originalCommonFiltersData,
    filterApplicability,
    liveEventNamesArray,
    handleCommonFiltersChange,
  } = useLiveEventsCommonFilters({
    selectedGameIds: gameIdArray,
    trackedEvents: trackedEvents,
    parsedParams: {
      liveEventTags: parsedParams.liveEventTags,
      liveEventSecondaryTags: parsedParams.liveEventSecondaryTags,
      motivations: parsedParams.motivations,
      archetypes: parsedParams.archetypes,
      liveEventAppearances: parsedParams.liveEventAppearances,
    },
    onFiltersChange: (filters) => {
      // Save filters to URL params for bookmark persistence
      setSearchParams((currentParams) => ({
        ...currentParams,
        liveEventTags: filters.liveEventTags.map((tag) => tag.id),
        liveEventSecondaryTags: filters.liveEventSecondaryTags.map((tag) => tag.id),
        motivations: Object.keys(filters.motivations),
        archetypes: Object.keys(filters.archetypes),
        liveEventAppearances: Object.keys(filters.liveEventAppearances),
      }))
    },
  })

  // Modify commonFiltersData to hide certain filter groups
  const commonFiltersData = useMemo(
    () => ({
      ...originalCommonFiltersData,
      additionalDatas: [],
      liveEventDurations: [],
      liveEventPricePoints: [],
    }),
    [originalCommonFiltersData]
  )

  // Default filters for Live Event Examples
  const defaultFilters = useMemo(
    () => ({
      liveEventTags: [],
      liveEventSecondaryTags: [],
      additionalDatas: {},
      liveEventDurations: {},
      liveEventAppearances: {},
      liveEventPricePoints: {},
      motivations: {},
      archetypes: {},
    }),
    []
  )

  // Filtered live events data
  const { filteredTrackingEventsByGame, eventNameSearchValue, handleEventNameSearchValueChange } = useLiveEventsFilteredData({
    selectedGames: trackedGameArray,
    trackedEvents: trackedEvents,
    commonFilters,
    eventNameSearchValue: '',
  })

  const isLoadingLiveEvents = isTrackedGameLoading || trackedEvents.isLoading

  if (!hasAccessToLiveEventsTracker) {
    return <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventsTracker} />
  }

  if (isTrackedGameLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>{t('common:loading')}</Typography>
      </Box>
    )
  }

  if (!trackedGame) {
    return <Typography>{t('implementations:no_live_events_found')}</Typography>
  }

  if (trackedGame.allowed === false) {
    return <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventsTracker} />
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: 0, mb: 0 }}>
          <Typography variant="h3" color="primary" gutterBottom>
            {t('implementations:label_filter_live_events')}
          </Typography>
          <Typography mb={2}>{t('implementations:label_description_filter_live_events')}</Typography>
          <LiveEventsViewFilters
            selectedGames={trackedGameArray}
            commonFilters={commonFilters}
            commonFiltersData={commonFiltersData}
            filterApplicability={filterApplicability}
            defaultFilters={defaultFilters}
            eventNameSearchValue={eventNameSearchValue}
            liveEventNames={liveEventNamesArray}
            onFiltersChange={handleCommonFiltersChange}
            onEventNameSearchValueChange={handleEventNameSearchValueChange}
          />
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <LiveEventsList
          selectedGames={trackedGameArray}
          filteredEvents={filteredTrackingEventsByGame.events as { [gameId: string]: any[] }}
          isLoading={isLoadingLiveEvents}
        />
      </Box>
    </>
  )
}

export default LiveEventExamplesSubtab
